import React, { useEffect, useState, useRef } from "react";

import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import TailwindModal from "./TailwindModal";

import { DefaultEditor } from "react-simple-wysiwyg";
import { toast } from "react-toastify";
import moment from "moment";

import {
  BriefcaseBusiness,
  Check,
  CheckCircle2,
  Clock3,
  Code2,
  Database,
  FileText,
  Info,
  Layers3,
  Loader2,
  Plus,
  Server,
  X,
  AlertCircle,
} from "lucide-react";

import "../css/DefaultEditor.css";

const domainConfig = {
  "application development": {
    icon: Code2,
    iconClass: "bg-blue-50 text-blue-600",
    selectedClass: "border-blue-400 bg-blue-50/40",
    checkClass: "bg-blue-600",
  },

  "devops & infra": {
    icon: Server,
    iconClass: "bg-orange-50 text-orange-600",
    selectedClass: "border-orange-400 bg-orange-50/40",
    checkClass: "bg-orange-500",
  },

  "data engineering": {
    icon: Database,
    iconClass: "bg-emerald-50 text-emerald-600",
    selectedClass: "border-emerald-400 bg-emerald-50/40",
    checkClass: "bg-emerald-500",
  },
};

const domainDescription = {
  "application development": "Build features & applications",
  "devops & infra": "Manage infra & deployments",
  "data engineering": "Build pipelines & process data",
};

const domainOptions = [
  {
    value: "Application Development",
    label: "Application Development",
  },
  {
    value: "DevOps & Infra",
    label: "DevOps & Infra",
  },
  {
    value: "Data Engineering",
    label: "Data Engineering",
  },
];

const statusOptions = [
  {
    value: "In-Progress",
    label: "In Progress",
  },
  {
    value: "Completed",
    label: "Completed",
  },
];

/* -------------------------------------------------------
   COMMON FIELD STYLE
------------------------------------------------------- */

const fieldSx = {
  width: "100%",

  "& .MuiOutlinedInput-root": {
    height: "40px",
    minHeight: "40px",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    fontSize: "13px",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",

    "& fieldset": {
      borderColor: "#cbd5e1",
      borderWidth: "1px",
    },

    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#3b82f6",
      borderWidth: "1.5px",
    },

    "&.Mui-disabled": {
      backgroundColor: "#f8fafc",
    },

    "&.Mui-error fieldset": {
      borderColor: "#ef4444 !important",
      borderWidth: "1.5px !important",
    },
    "&.Mui-error:hover fieldset": {
      borderColor: "#ef4444 !important",
    },
    "&.Mui-error.Mui-focused fieldset": {
      borderColor: "#ef4444 !important",
    }
  },

  "& .MuiInputBase-input": {
    boxSizing: "border-box",
    fontSize: "13px",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
  },

  "& .MuiAutocomplete-inputRoot": {
    height: "40px",
    minHeight: "40px",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "14px !important",
  },

  "& .MuiAutocomplete-input": {
    boxSizing: "border-box",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
  },

  "& .MuiInputAdornment-root": {
    marginTop: "0 !important",
  },
  "& input::placeholder": {
    color: "#8795a8",
    opacity: 1,
    fontWeight: 400,
  },

  "& .MuiInputBase-input::placeholder": {
    color: "#8795a8 !important",
    opacity: "1 !important",
  },
};

/* -------------------------------------------------------
   LABEL
------------------------------------------------------- */

const FieldLabel = ({ children }) => (
  <label className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-900">
    {children}

    <span className="text-red-500">*</span>
  </label>
);

/* -------------------------------------------------------
   COMPONENT
------------------------------------------------------- */

const AddTaskModal = ({
  isOpen,
  onClose,
  onSave,
  modalHeading,
  projects,
  taskTypeOptions,
  initialData,
  taskTypeMap,
  isLoading = false,
  selectedDate,
}) => {
  const initialState = {
    projectCode: [],
    projectName: "",
    taskName: "",
    domain: "Application Development",
    description: "",
    status: "",
    hours: "",
    taskType: "",
  };

  const [subTasks, setSubTasks] = useState(initialState);
  const [validationError, setValidationError] = useState("");
  const hoursInputRef = useRef(null);

  /* -------------------------------------------------------
     PROJECT OPTIONS
  ------------------------------------------------------- */

  const PrjListOption =
    projects
      ?.filter((item) => item.PROJECT_STATUS === "Open")
      .sort((a, b) => (a.PROJECT_NAME > b.PROJECT_NAME ? 1 : -1))
      .map((item) => ({
        value: `${item.PROJECT_CODE},${item.PROJECT_NAME}`,
        label: item.PROJECT_NAME,
      })) || [];

  /* -------------------------------------------------------
     RESET / EDIT DATA
  ------------------------------------------------------- */

  useEffect(() => {
    if (!isOpen) return;

    setValidationError("");

    if (initialData) {
      setSubTasks(initialData);
    } else {
      setSubTasks(initialState);
    }
  }, [isOpen, initialData]);

  /* -------------------------------------------------------
     PREVENT MOUSE SCROLL ON HOURS
  ------------------------------------------------------- */

  useEffect(() => {
    let timer;
    const handleWheel = (e) => {
      if (document.activeElement === e.target) {
        // Temporarily make readOnly to prevent value change
        // This allows the wheel event to still scroll the outer container natively
        e.target.readOnly = true;
        clearTimeout(timer);
        timer = setTimeout(() => {
          e.target.readOnly = false;
        }, 50);
      }
    };
    
    const inputElement = hoursInputRef.current;
    if (inputElement) {
      inputElement.addEventListener("wheel", handleWheel, { passive: true });
    }
    return () => {
      if (inputElement) {
        inputElement.removeEventListener("wheel", handleWheel);
      }
      clearTimeout(timer);
    };
  }, [isOpen]);

  /* -------------------------------------------------------
     TASK TYPES
  ------------------------------------------------------- */

  const filteredTaskTypeOptions =
    taskTypeOptions?.filter((option) =>
      taskTypeMap?.[subTasks?.domain]?.includes(option.value),
    ) || [];

  /* -------------------------------------------------------
     COMMON FIELD UPDATE
  ------------------------------------------------------- */

  const updateField = (field, value) => {
    setSubTasks((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /* -------------------------------------------------------
     DOMAIN
  ------------------------------------------------------- */

  const handleDomainChange = (value) => {
    setSubTasks((prev) => ({
      ...prev,
      domain: value,
      taskType: "",
    }));
  };

  /* -------------------------------------------------------
     SAVE
  ------------------------------------------------------- */

  const handleSave = () => {
    if (
      subTasks?.projectCode?.length > 0 &&
      subTasks?.taskName?.length > 0 &&
      subTasks?.domain?.length > 0 &&
      subTasks?.description?.length > 0 &&
      subTasks?.status?.length > 0 &&
      Number(subTasks?.hours) > 0 &&
      subTasks?.taskType?.length > 0
    ) {
      setValidationError("");
      onSave(subTasks);
      return;
    }

    setValidationError("Please fill out all the fields");
  };

  return (
    <TailwindModal
      isOpen={isOpen}
      toggle={!isLoading ? onClose : undefined}
      maxWidth={"820px"}
    >
      {/* ================= HEADER ================= */}

      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50 text-indigo-600 shadow-sm">
            <BriefcaseBusiness size={17} strokeWidth={2.2} />
          </div>

          <div>
            <h2 className="m-0 text-[16px] font-semibold tracking-tight text-slate-900">
              {modalHeading} Task {selectedDate ? (moment(selectedDate).isSame(moment(), 'day') ? 'for Today' : `for ${moment(selectedDate).format("DD MMM YYYY")}`) : ""}
            </h2>

            <p className="m-0 mt-0.5 text-[11px] text-slate-500">
              Add task details and track your progress
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={17} />
        </button>
      </div>

      {/* ================= BODY ================= */}

      <div
        className="px-[22px] py-[18px] overflow-y-auto"
        style={{ maxHeight: "75vh" }}
      >
        <div className="space-y-3.5">
          {/* PROJECT */}

          <div>
            <FieldLabel>Project</FieldLabel>
            <Autocomplete
              options={PrjListOption}
              openOnFocus
              getOptionLabel={(option) => option.label}
              value={
                PrjListOption.find(
                  (option) =>
                    option.value ===
                    `${subTasks.projectCode},${subTasks.projectName}`,
                ) || null
              }
              slotProps={{
                popper: {
                  sx: {
                    zIndex: "11000 !important",
                  },
                },
              }}
              onChange={(event, newValue) => {
                if (!newValue) {
                  setSubTasks((prev) => ({
                    ...prev,
                    projectCode: [],
                    projectName: "",
                  }));

                  return;
                }

                const [projectCode, ...projectName] = newValue.value.split(",");

                setSubTasks((prev) => ({
                  ...prev,
                  projectCode,
                  projectName: projectName.join(","),
                }));
              }}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "13px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select a project"
                  size="small"
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,

                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <BriefcaseBusiness
                            size={15}
                            className="text-slate-400"
                          />
                        </InputAdornment>

                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>

          {/* DOMAIN */}

          <div>
            <div>
              <FieldLabel>Domain</FieldLabel>

              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                {domainOptions.map((option) => {
                  const key = option.value.toLowerCase();
                  const config = domainConfig[key] || {};
                  const Icon = config.icon || BriefcaseBusiness;
                  const isSelected = subTasks.domain === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleDomainChange(option.value)}
                      className={`
            group relative flex min-h-[76px] items-center gap-3
            rounded-xl border px-3 py-3 text-left
            transition-all duration-200
            ${
              isSelected
                ? `${config.selectedClass} shadow-sm`
                : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
            }
          `}
                    >
                      <div
                        className={`
              flex h-9 w-9 shrink-0 items-center justify-center
              rounded-lg
              ${config.iconClass}
            `}
                      >
                        <Icon size={17} strokeWidth={2} />
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="m-0 pr-1 text-[13px] font-semibold leading-[18px] text-slate-800">
                          {option.label}
                        </p>

                        <p className="m-0 mt-1 text-[11px] leading-[15px] text-slate-500">
                          {domainDescription[key]}
                        </p>
                      </div>

                      <div
                        className={`
              flex h-5 w-5 shrink-0 items-center justify-center
              rounded-full border transition-all duration-200
              ${
                isSelected
                  ? `${config.checkClass} border-transparent text-white`
                  : "border-slate-300 bg-white"
              }
            `}
                      >
                        {isSelected && <Check size={11} strokeWidth={3} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* TASK TYPE */}

          <div>
            <FieldLabel>Task Type</FieldLabel>
            <Autocomplete
              openOnFocus
              options={filteredTaskTypeOptions}
              getOptionLabel={(option) => option.label}
              value={
                filteredTaskTypeOptions?.find(
                  (option) => option.value === subTasks.taskType,
                ) || null
              }
              slotProps={{
                popper: {
                  sx: {
                    zIndex: "11000 !important",
                  },
                },
              }}
              disabled={!subTasks.domain}
              onChange={(event, newValue) =>
                updateField("taskType", newValue?.value || "")
              }
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "13px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select task type"
                  size="small"
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,

                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Layers3 size={15} className="text-slate-400" />
                        </InputAdornment>

                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>

          {/* STATUS + HOURS */}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <FieldLabel>Status</FieldLabel>

              <Autocomplete
                openOnFocus
                options={statusOptions}
                getOptionLabel={(option) => option.label}
                value={
                  statusOptions.find(
                    (option) => option.value === subTasks.status,
                  ) || null
                }
                slotProps={{
                  popper: {
                    sx: {
                      zIndex: "11000 !important",
                    },
                  },
                }}
                onChange={(event, newValue) =>
                  updateField("status", newValue?.value || "")
                }
                ListboxProps={{
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "13px",
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select status"
                    size="small"
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,

                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <CheckCircle2
                              size={15}
                              className="text-slate-400"
                            />
                          </InputAdornment>

                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>

            <div>
              <FieldLabel>Hours Spent</FieldLabel>

              <TextField
                type="number"
                inputRef={hoursInputRef}
                value={subTasks.hours}
                error={Number(subTasks.hours) > 12}
                fullWidth
                size="small"
                placeholder="Enter hours"
                onChange={(event) => {
                  let value = event.target.value;

                  if (value !== "") {
                    value = Math.max(1, Number(value));
                  }

                  updateField("hours", value);
                }}
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Clock3 size={15} className="text-slate-400" />
                    </InputAdornment>
                  ),
                }}
              />
              {Number(subTasks.hours) > 12 && (
                <p className="mt-1 text-[11px] font-medium text-red-500">
                  Hours cannot exceed 12
                </p>
              )}
            </div>
          </div>

          {/* TASK DETAILS */}

          <div>
            <FieldLabel>Task Details</FieldLabel>

            <TextField
              value={subTasks.taskName}
              onChange={(event) => updateField("taskName", event.target.value)}
              placeholder="What did you work on? (e.g., Implemented the editorial grid system)"
              size="small"
              fullWidth
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FileText size={15} className="text-slate-400" />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          {/* DESCRIPTION */}

          <div>
            <FieldLabel>Description</FieldLabel>

            <div className="custom-editor">
              <DefaultEditor
                value={subTasks.description}
                onChange={(event) =>
                  updateField("description", event.target.value)
                }
                className="TextEditor"
                style={{ height: "120px", overflow: "auto" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ================= FOOTER ================= */}

      <div className="flex items-center justify-between border-t border-slate-200 bg-[#fafbfc] px-[22px] py-[12px]">
        {validationError ? (
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-red-500">
            <AlertCircle size={14} />
            <span>{validationError}</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-[10.5px] text-slate-500">
            <Info size={13} />

            <span>
              Fields marked with{" "}
              <span className="font-semibold text-red-500">*</span> are required
            </span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            onClick={onClose}
            disabled={isLoading}
            variant="outlined"
            sx={{
              height: "34px",
              borderColor: "#cbd5e1",
              color: "#475569",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              padding: "0 15px",
              fontSize: "12px",
              whiteSpace: "nowrap",

              "&:hover": {
                borderColor: "#94a3b8",
                backgroundColor: "#f8fafc",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            disabled={isLoading || Number(subTasks.hours) > 12}
            variant="contained"
            disableElevation
            startIcon={
              isLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Plus size={14} strokeWidth={2.5} />
              )
            }
            sx={{
              height: "34px",
              backgroundColor: "#2563eb",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              padding: "0 17px",
              fontSize: "12px",
              whiteSpace: "nowrap",

              "&:hover": {
                backgroundColor: "#1d4ed8",
              },
            }}
          >
            {isLoading
              ? "Saving..."
              : modalHeading === "Edit"
                ? "Save Changes"
                : "Add Task"}
          </Button>
        </div>
      </div>
    </TailwindModal>
  );
};

export default AddTaskModal;
